import { useCallback, useMemo, type ReactElement } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { FALLBACK_CONFIG, useConfigQuery } from '~/api/hooks'
import { NS } from './index'

type Values = Record<string, string | number>

/** `t()` with the organisation placeholders of copy v7 (`<Org>`, `<contact>`, `<phone>`) always filled in. */
export function useT() {
  const { t } = useTranslation(NS)
  const config = useConfigQuery().data ?? FALLBACK_CONFIG
  const base: Values = useMemo(
    () => ({
      org: config.org.name,
      contact: config.org.contact,
      phone: config.org.phone,
      controller: config.org.controllerName,
      days: config.reviewMediaHoldDays,
    }),
    [config]
  )
  const tt = useCallback((key: string, values: Values = {}) => t(key, { ...base, ...values }) as string, [t, base])
  /** A list of strings (an array in en.json). */
  const list = useCallback(
    (key: string, values: Values = {}) => t(key, { ...base, ...values, returnObjects: true }) as unknown as string[],
    [t, base]
  )
  return { config, t: tt, list, base }
}

/** Rich text: `<b>…</b>` and other tags in the string are replaced by the given elements. */
export function Rich({
  k,
  values = {},
  components = {},
}: {
  k: string
  values?: Values
  components?: Record<string, ReactElement>
}) {
  const { base } = useT()
  return (
    <Trans i18nKey={k} ns={NS} values={{ ...base, ...values }} components={{ b: <b />, i: <i />, ...components }} />
  )
}
